/* Progressively enhance the native cart form with serialized, server-priced updates. */
(() => {
  'use strict';
  function initCart(page) {
  if (page.r2Cart) return;
  const root = window.Shopify?.routes?.root || '/';
  const sectionId = page.dataset.sectionId;
  const status = page.querySelector('[data-r2-cart-status]');
  const content = page.querySelector('[data-r2-cart-content]');
  const inputs = () => [...content.querySelectorAll('[data-r2-cart-quantity]')];
  const valid = input => input.value !== '' && input.checkValidity() && Number.isSafeInteger(Number(input.value));
  const dirty = () => inputs().some(input => input.value !== input.dataset.savedQuantity);
  const identity = input => input.dataset.lineIdentity;
  let timer, flight, failed = false, leaving = false, nativeSubmit = false;

  function announce(key, error = false, detail = '') {
    status.textContent = detail || page.dataset[key === 'updating' ? 'updatingMessage' : key] || '';
    status.classList.toggle('r2-cart-error', error);
  }
  function setBusy(busy) {
    page.toggleAttribute('data-updating', busy);
    content.setAttribute('aria-busy', String(busy));
  }
  function matching(input, candidates) {
    const exact = candidates.find(other => other.dataset.lineKey === input.dataset.lineKey);
    if (exact) return exact;
    // Discounts can change line keys. Only use a unique variant/properties/plan match.
    const same = candidates.filter(other => identity(other) === identity(input));
    return same.length === 1 ? same[0] : null;
  }
  const sectionUrl = () => {
    const url = new URL(location.href);
    url.searchParams.set('sections', sectionId);
    return url;
  };
  async function sectionHTML(cart) {
    if (cart.sections?.[sectionId]) return cart.sections[sectionId];
    const response = await fetch(sectionUrl(), {cache: 'no-store'});
    if (!response.ok) throw new Error('Section unavailable');
    const sections = await response.json();
    if (!sections[sectionId]) throw new Error('Section unavailable');
    return sections[sectionId];
  }
  async function render(cart, sent) {
    const html = await sectionHTML(cart);
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-r2-cart-content]');
    if (!next) throw new Error('Cart section unavailable');
    const oldInputs = inputs(), nextInputs = [...next.querySelectorAll('[data-r2-cart-quantity]')];
    const active = document.activeElement;
    const hadFocus = content.contains(active);
    let focus, adjusted = false;
    for (const input of oldInputs) {
      const replacement = matching(input, nextInputs);
      const isSent = input.dataset.lineKey === sent.key;
      // Never replace edits made while the previous request was in flight.
      const pending = isSent ? input.value !== sent.value : input.value !== input.dataset.savedQuantity;
      if (pending && replacement) replacement.value = input.value;
      if (isSent && input.value === sent.value && replacement && Number(replacement.dataset.savedQuantity) !== Number(sent.value)) adjusted = true;
      if (replacement && (active === input || active?.closest('.r2-quantity-stepper')?.contains(input))) {
        focus = active === input ? replacement : replacement.closest('.r2-quantity-stepper')?.querySelector('[data-r2-quantity-step="' + active.dataset.r2QuantityStep + '"]') || replacement;
      }
    }
    const scroll = content.querySelector('.r2-drawer-items')?.scrollTop || 0;
    content.replaceChildren(...next.childNodes);
    const scroller = content.querySelector('.r2-drawer-items');
    if (scroller) scroller.scrollTop = scroll;
    if (focus) focus.focus({preventScroll: true});
    else if (hadFocus && !active.isConnected) page.closest('dialog')?.querySelector('[data-r2-close]')?.focus({preventScroll:true});
    page.querySelector('[data-r2-cart-heading-count]').textContent = '(' + cart.item_count + ')';
    document.querySelectorAll('[data-r2-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.closest('a')?.setAttribute('aria-label', page.dataset.countLabel.replace('__count__', cart.item_count));
    });
    return adjusted;
  }
  async function drain() {
    failed = false;
    setBusy(true);
    announce('updating');
    try {
      while (true) {
        const input = inputs().find(el => valid(el) && el.value !== el.dataset.savedQuantity);
        if (!input) break;
        const sent = {key: input.dataset.lineKey, value: input.value};
        // Zero removes the row; prevent an edit to a row that is being deleted.
        if (Number(sent.value) === 0) input.disabled = true;
        try {
          const response = await fetch(root + 'cart/change.js', {
            method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
            body: JSON.stringify({id: sent.key, quantity: Number(sent.value), sections: [sectionId], sections_url: location.pathname + location.search}),
            keepalive: true
          });
          const cart = await response.json();
          if (!response.ok) {
            const error = new Error(cart.description || page.dataset.error);
            error.cartRejected = true;
            throw error;
          }
          if (await render(cart, sent)) {
            failed = true;
            announce('adjusted', true);
            break;
          }
        } catch (error) {
          // Shopify can change quantities even when returning an error. Reconcile
          // actual prices and quantities before reporting the unsuccessful change.
          try {
            const response = await fetch(root + 'cart.js', {cache: 'no-store'});
            if (response.ok) await render(await response.json(), sent);
          } catch (_) { /* Retain unsaved values so Update cart can retry. */ }
          failed = true;
          announce('error', true, error.cartRejected ? error.message : '');
          break;
        } finally { input.disabled = false; }
      }
      if (!failed) announce(dirty() ? 'invalid' : 'updated', dirty());
    } finally { setBusy(false); }
    return !failed && !dirty();
  }
  function save() {
    clearTimeout(timer);
    if (flight) return flight;
    if (!dirty()) return Promise.resolve(!failed);
    flight = drain().finally(() => { flight = null; });
    return flight;
  }
  function schedule(event) {
    if (!event.target.matches('[data-r2-cart-quantity]')) return;
    failed = false;
    clearTimeout(timer);
    if (!valid(event.target)) {
      announce('invalid', true);
      return;
    }
    if (!dirty() && !flight) { announce('updated'); return; }
    announce('updating');
    if (event.type === 'change') save();
    else timer = setTimeout(save, 350);
  }
  page.addEventListener('input', schedule);
  page.addEventListener('change', schedule);
  page.addEventListener('click', event => {
    const step = event.target.closest('[data-r2-quantity-step]');
    if (step) {
      const input = step.parentElement.querySelector('[data-r2-cart-quantity]');
      if (input.disabled) return;
      input.value = String(Math.max(0,(Number(input.value)||0)+Number(step.dataset.r2QuantityStep)));
      input.dispatchEvent(new Event('input',{bubbles:true}));
      return;
    }
    const remove = event.target.closest('[data-r2-cart-remove]');
    if (!remove || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const input = remove.closest('.r2-cart-item').querySelector('[data-r2-cart-quantity]');
    if (input.disabled) return;
    input.value = '0'; failed = false; save();
  });
  page.addEventListener('submit', async event => {
    if (nativeSubmit || !event.target.matches('.r2-cart-layout')) return;
    const checkout = event.submitter?.name === 'checkout';
    // An already-saved cart must retain the browser's original native checkout
    // submission. Re-entering requestSubmit within the same submission event
    // can be ignored by the browser, even after awaiting a resolved promise.
    if (checkout && !dirty() && !flight && !failed) return;
    event.preventDefault();
    if (leaving) return;
    leaving = checkout;
    failed = false;
    const saved = await save();
    if (saved && checkout) {
      // Wait for the original submit event to finish before resubmitting the
      // latest server-rendered form. Keep native validation and checkout fields.
      setTimeout(() => {
        const form = content.querySelector('form');
        if (form) {
          nativeSubmit = true;
          form.requestSubmit(form.querySelector('[name="checkout"]'));
          nativeSubmit = false;
        }
        leaving = false;
      }, 0);
      return;
    }
    leaving = false;
  });
  // Save before ordinary storefront navigation, including a click immediately
  // after typing. Modified clicks/new tabs keep normal browser behavior.
  document.addEventListener('click', async event => {
    const link = event.target.closest('a[href]');
    if (!link || link.matches('[data-r2-cart-remove]') || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
    const url = new URL(link.href);
    // Drawer openers are actions on this page. Its refresh waits for pending
    // quantity saves; treating the opener as navigation would leave the product.
    if (page.hasAttribute('data-r2-drawer-page') && url.origin === location.origin && url.pathname === root + 'cart' && !link.hasAttribute('data-r2-cart-full')) return;
    if (url.origin !== location.origin || (url.pathname === location.pathname && url.search === location.search && url.hash)) return;
    if (!dirty() && !flight) return;
    event.preventDefault();
    if (leaving) return;
    leaving = true;
    if (await save()) location.assign(url.href);
    else leaving = false;
  });
  // Browser reload/close cannot await promises; protect genuinely unsaved edits.
  window.addEventListener('beforeunload', event => {
    if (dirty() || flight) { event.preventDefault(); event.returnValue = ''; }
  });
  window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
  let refreshing;
  page.r2Cart = {save, busy:()=>Boolean(flight||dirty()), refresh:(sections)=>{
    if(refreshing) return refreshing;
    refreshing=(async()=>{
      if((dirty()||flight) && !await save()) return false;
      const fallback=page.querySelector('.r2-drawer-fallback');
      page.setAttribute('data-refreshing','');content.setAttribute('aria-busy','true');content.inert=true;content.hidden=false;
      if(fallback)fallback.hidden=true;
      announce('updating');
      try{
        const response=await fetch(root+'cart.js',{cache:'no-store'});
        if(!response.ok)throw Error('Cart unavailable');
        const cart = await response.json();
        if(sections) cart.sections = sections;
        await render(cart,{key:null,value:null});
        failed=false;announce('updated');return true;
      }catch(_){failed=true;announce('error',true);content.hidden=true;if(fallback)fallback.hidden=false;return false;}
      finally{
        page.removeAttribute('data-refreshing');content.removeAttribute('aria-busy');content.inert=false;
        if(!content.hidden && page.closest('dialog[open]') && !matchMedia('(prefers-reduced-motion: reduce)').matches && content.animate){
          content.animate([{opacity:0,transform:'translateY(8px)'},{opacity:1,transform:'translateY(0)'}],{duration:300,easing:'ease-out'});
        }
      }
    })().finally(()=>{refreshing=null;});
    return refreshing;
  }};
  }
  document.querySelectorAll('[data-r2-cart-page]').forEach(initCart);
})();
